import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Tooltip,
  Divider,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
  alpha,
  useTheme,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import axios from '../../../utils/axios';
import MyLogo from '../../../components/userlogo';

// ----------------------------------------------------------------------
export default function NotificationsPopover() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [notifications, setNotifications] = useState([]);
  const totalUnRead = notifications?.filter((item) => item?.status === 0).length;
  const [open, setOpen] = useState(null);
  const AccessToken = window.localStorage.getItem('accessToken');

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  useEffect(() => {
    axios.get(`/api/system/notification/user/${AccessToken}/request/user`).then((data) => {
      setNotifications(data.data.notif);
    });
  }, [AccessToken]);

  const handleClose = () => {
    setOpen(null);
  };

  const handleMarkAllAsRead = async () => {
    await axios(`/api/clear/notification/clear/all/${AccessToken}/by/user`).then(
      setNotifications(
        notifications?.map((notification) => ({
          ...notification,
          status: 1,
        }))
      ),
      enqueueSnackbar('Marked Read Successfully')
    );
  };

  return (
    <>
      <IconButtonAnimate
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{
          width: 40,
          height: 40,
          position: 'relative',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            transform: 'scale(1.05)',
            transition: 'all 0.2s ease-in-out',
          },
        }}
      >
        <Badge
          badgeContent={totalUnRead}
          color="error"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiBadge-badge': {
              animation: totalUnRead > 0 ? 'pulse 2s infinite' : 'none',
              right: 0,
              top: 3,
              border: `2px solid ${theme.palette.background.paper}`,
              padding: '0 3px',
              minWidth: '16px',
              height: '16px',
              fontSize: '0.61rem',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                },
                '50%': {
                  transform: 'scale(1.1)',
                },
                '100%': {
                  transform: 'scale(1)',
                },
              },
            },
          }}
        >
          <Iconify
            icon="mdi:bell"
            width={28}
            height={28}
            sx={{
              color: open ? theme.palette.primary.main : theme.palette.text.primary,
              opacity: 0.9,
            }}
          />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          width: 380,
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiPaper-root': {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        }}
      >
        {/* Header with Solid Background */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 3,
            px: 3,
            bgcolor: theme.palette.primary.main,
            color: 'white',
            position: 'relative',
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              🔔 Notifications
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {totalUnRead > 0 ? `You have ${totalUnRead} unread messages` : 'All caught up!'}
            </Typography>
          </Box>
          {totalUnRead > 0 && (
            <Tooltip title="Mark all as read">
              <IconButtonAnimate
                onClick={handleMarkAllAsRead}
                sx={{
                  color: 'white',
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.3),
                    transform: 'rotate(15deg) scale(1.1)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButtonAnimate>
            </Tooltip>
          )}
        </Box>

        {/* Divider */}
        <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.3), mx: 2 }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          {/* New Notifications Section */}
          <List
            disablePadding
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  py: 2,
                  px: 3,
                  typography: 'subtitle2',
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                Recent
              </ListSubheader>
            }
          >
            {notifications?.slice(0, 2).map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </List>

          {/* Older Notifications Section */}
          <List
            disablePadding
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  py: 2,
                  px: 3,
                  typography: 'subtitle2',
                  color: theme.palette.text.secondary,
                  bgcolor: alpha(theme.palette.grey[500], 0.05),
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                Earlier
              </ListSubheader>
            }
          >
            {notifications?.slice(2, 5).map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </List>
        </Scrollbar>

        {/* Divider */}
        <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.3), mx: 2 }} />

        {/* Footer Button */}
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            disableRipple
            component={RouterLink}
            to={PATH_DASHBOARD.general.notif}
            onClick={handleClose}
            sx={{
              py: 1.5,
              borderRadius: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            📱 View All Notifications
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------
NotificationItem.propTypes = {
  notification: PropTypes.shape({
    date: PropTypes.string,
    id: PropTypes.number,
    title: PropTypes.string,
    status: PropTypes.number,
    description: PropTypes.string,
    type: PropTypes.string,
    avatar: PropTypes.any,
    profile_image: PropTypes.any,
  }),
};

function NotificationItem({ notification }) {
  const theme = useTheme();
  const { title } = renderContent(notification, theme); // Pass theme as an argument
  return (
    <ListItemButton
      sx={{
        py: 2,
        px: 3,
        mx: 1,
        mb: 1,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        ...(notification?.status === 0 && {
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            bgcolor: theme.palette.primary.main,
            borderRadius: '0 2px 2px 0',
          },
        }),
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          transform: 'translateX(4px)',
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
        },
      }}
    >
      <ListItemAvatar>
        <Box
          sx={{
            position: 'relative',
            '&::after':
              notification?.status === 0
                ? {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 12,
                    height: 12,
                    bgcolor: theme.palette.error.main,
                    borderRadius: '50%',
                    border: `2px solid ${theme.palette.background.paper}`,
                    animation: 'pulse 2s infinite',
                  }
                : {},
          }}
        >
          <MyLogo username={notification?.profile_image} />
        </Box>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                bgcolor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <Iconify
                icon="eva:clock-outline"
                sx={{
                  width: 16,
                  height: 16,
                  color: theme.palette.info.main,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.info.main,
                  fontWeight: 'medium',
                  whiteSpace: 'nowrap',
                }}
              >
                {notification?.date ? format(new Date(notification.date), 'dd MMM yyyy, hh:mm a') : ''}
              </Typography>
            </Box>
          </Box>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------
function renderContent(notification, theme) {
  const title = (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 'bold',
          color: theme.palette.text.primary,
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1,
            py: 0.3,
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.main,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 'bold',
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
          }}
        >
          🔔 SYSTEM
        </Box>
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          lineHeight: 1.4,
          fontWeight: 'medium',
        }}
      >
        {notification?.message}
      </Typography>
    </Box>
  );
  return {
    title,
  };
}
